import { useEffect, useState, type FormEvent } from "react";
import { PASSWORD_MIN } from "@krut/shared";
import { authClient } from "../api.ts";
import { useBrand, useMe, usePublicConfig } from "../hooks.ts";
import { Logo } from "../components/Shell.tsx";
import { Button, Card, ErrorText, Field, Input, Spinner } from "../components/ui.tsx";
import { safeNext } from "../lib/next.ts";

/** Mnemio's memory stack, drawn large and faint: three bars, the newest in agent blue. */
function MemoryBars() {
  return (
    <div aria-hidden="true" className="space-y-3">
      <div className="h-3 w-40 rounded-full bg-white/[0.12]" />
      <div className="h-3 w-28 rounded-full bg-white/[0.08]" />
      <div className="h-3 w-40 rounded-full bg-[var(--color-brand)]/80" />
    </div>
  );
}

const providerMeta = {
  google: {
    label: "Continue with Google",
    icon: (
      <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
    ),
  },
  microsoft: {
    label: "Continue with Microsoft",
    icon: (
      <svg width="18" height="18" viewBox="0 0 23 23"><path fill="#f35325" d="M1 1h10v10H1z"/><path fill="#81bc06" d="M12 1h10v10H12z"/><path fill="#05a6f0" d="M1 12h10v10H1z"/><path fill="#ffba08" d="M12 12h10v10H12z"/></svg>
    ),
  },
} as const;

export function LoginPage() {
  const cfg = usePublicConfig();
  const brand = useBrand();
  const me = useMe();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(() => new URLSearchParams(location.search).get("error"));
  const params = new URLSearchParams(location.search);
  // "next" from the app's own pages; "rd" is what the ingress adds when the memory explorer asks for sign-in.
  const next = safeNext(params.get("next") ?? params.get("rd"), cfg.data?.returnHosts ?? []);
  const external = !next.startsWith("/");
  // Already signed in (for example coming back from Google, or sent here by the memory explorer): go on.
  useEffect(() => {
    if (!me.data || !cfg.data) return;
    if (external) location.replace(next);
    else if (next !== "/login" && !next.startsWith("/login?")) location.replace(next);
  }, [me.data, cfg.data, next, external]);
  if (me.data && cfg.data) return <div className="grid min-h-full place-items-center"><Spinner /></div>;

  return (
    <div className="flex min-h-full flex-col lg:flex-row">
      {/* The install's logo on its sidebar colour: a slim header on phones, a full panel beside the form on wide screens. */}
      <div className="chrome-glow relative flex shrink-0 flex-col justify-between overflow-hidden bg-[var(--chrome)] px-6 py-6 lg:w-[44%] lg:max-w-xl lg:px-14 lg:py-12">
        <div className="flex"><Logo className="h-7 lg:h-8" /></div>
        <div className="relative hidden lg:block">
          <MemoryBars />
          <p className="mt-10 max-w-sm font-display text-[32px] leading-[1.15] font-medium tracking-[-0.02em] text-white">Your AI assistant for work.</p>
          <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-[var(--chrome-text)]">It remembers what your team knows and works in the apps you already use.</p>
        </div>
        <span className="hidden lg:block" />
      </div>
      <div className="grid flex-1 place-items-center px-4 py-12 sm:px-6">
      <Card className="w-full max-w-sm p-8">
        <div>
        <h1 className="text-[26px] leading-tight font-semibold">Sign in</h1>
        <p className="mt-1.5 text-sm text-[var(--muted)]">{external ? `Sign in with your administrator account to open the memory explorer.` : "Use your work account to reach your AI assistant."}</p>
        <div className="mt-6 space-y-2.5">
          {cfg.isPending && <div className="grid place-items-center py-4"><Spinner /></div>}
          {cfg.data?.providers.map((p) => (
            <button
              key={p}
              disabled={!!busy}
              onClick={async () => {
                setBusy(p);
                setError(null);
                // Another host can't be the sign-in callback: come back to this page, which then forwards there.
                const callbackURL = external ? `/login?next=${encodeURIComponent(next)}` : next;
                const r = await authClient.signIn.social({ provider: p, callbackURL, errorCallbackURL: "/login" });
                if (r.error) {
                  setError(r.error.message ?? "Sign-in failed");
                  setBusy(null);
                }
              }}
              className="flex h-11 w-full items-center justify-center gap-3 rounded-full border border-[var(--line)] bg-[var(--panel)] text-sm font-medium shadow-[var(--shadow-sm)] transition hover:border-[var(--line-strong)] hover:bg-[var(--panel-2)] disabled:opacity-60"
            >
              {busy === p ? <Spinner /> : providerMeta[p].icon}
              {providerMeta[p].label}
            </button>
          ))}
          {cfg.data?.passwordSignIn && <PasswordSignIn afterSso={cfg.data.providers.length > 0} onSignedIn={() => void me.refetch()} />}
          {cfg.data && cfg.data.providers.length === 0 && !cfg.data.passwordSignIn && (
            <p className="text-sm text-[var(--muted)]">No sign-in method is configured yet. An administrator needs to set one up.</p>
          )}
          <ErrorText error={error ? friendly(String(error instanceof Error ? error.message : error), brand.name) : null} />
        </div>
        </div>
      </Card>
      </div>
    </div>
  );
}

type Step = "sign-in" | "send-code" | "set-password";

/**
 * Email + password, when an admin has turned it on (Admin → Sign-in). There is no sign-up: someone who has an
 * account sets or resets their password with a code sent to their email, and is then signed in with it.
 */
function PasswordSignIn({ afterSso, onSignedIn }: { afterSso: boolean; onSignedIn: () => void }) {
  const brand = useBrand();
  const [step, setStep] = useState<Step>("sign-in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const go = (next: Step, message: string | null = null) => {
    setStep(next);
    setError(null);
    setNotice(message);
    setPassword("");
    setConfirm("");
    setCode("");
  };
  const submit = (fn: () => Promise<void>) => async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const signIn = async () => {
    const r = await authClient.signIn.email({ email, password });
    if (r.error) setError(passwordError(r.error, brand.name));
    else onSignedIn();
  };
  const sendCode = async () => {
    const r = await authClient.emailOtp.requestPasswordReset({ email });
    if (r.error) return setError(passwordError(r.error, brand.name));
    go("set-password", `If ${email} has an account here, we've emailed it a 6-digit code. It expires in 10 minutes.`);
  };
  const setNewPassword = async () => {
    if (password !== confirm) return setError("The two passwords don't match.");
    const r = await authClient.emailOtp.resetPassword({ email, otp: code.trim(), password });
    if (r.error) return setError(passwordError(r.error, brand.name));
    const s = await authClient.signIn.email({ email, password });
    if (s.error) go("sign-in", "Your password is set. Sign in with it.");
    else onSignedIn();
  };

  const link = "text-sm font-medium text-[var(--color-accent)] hover:underline disabled:opacity-50";
  return (
    <div className="space-y-4">
      {afterSso && (
        <div className="flex items-center gap-3 pt-2 text-xs text-[var(--muted)]">
          <span className="h-px flex-1 bg-[var(--line)]" />
          or with your email
          <span className="h-px flex-1 bg-[var(--line)]" />
        </div>
      )}
      {notice && <p className="rounded-lg bg-[var(--panel-2)] px-3 py-2 text-sm">{notice}</p>}
      {step === "sign-in" && (
        <form className="space-y-3" onSubmit={submit(signIn)}>
          <Field label="Email"><Input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <Field label="Password"><Input type="password" required autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} /></Field>
          <ErrorText error={error} />
          <Button type="submit" variant="primary" className="h-11 w-full" disabled={busy}>{busy ? <Spinner /> : "Sign in"}</Button>
          <button type="button" className={link} onClick={() => go("send-code")}>Set or reset your password</button>
        </form>
      )}
      {step === "send-code" && (
        <form className="space-y-3" onSubmit={submit(sendCode)}>
          <p className="text-sm text-[var(--muted)]">We'll email you a code. With it you can set a new password, or your first one.</p>
          <Field label="Email"><Input type="email" required autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
          <ErrorText error={error} />
          <Button type="submit" variant="primary" className="h-11 w-full" disabled={busy}>{busy ? <Spinner /> : "Email me a code"}</Button>
          <button type="button" className={link} onClick={() => go("sign-in")}>Back to sign-in</button>
        </form>
      )}
      {step === "set-password" && (
        <form className="space-y-3" onSubmit={submit(setNewPassword)}>
          <Field label="Code from the email">
            <Input required inputMode="numeric" autoComplete="one-time-code" maxLength={6} pattern="[0-9]{6}" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
          </Field>
          <Field label="New password" hint={`At least ${PASSWORD_MIN} characters, not one that has appeared in a data breach.`}>
            <Input type="password" required minLength={PASSWORD_MIN} autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Field>
          <Field label="New password again"><Input type="password" required autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} /></Field>
          <ErrorText error={error} />
          <Button type="submit" variant="primary" className="h-11 w-full" disabled={busy}>{busy ? <Spinner /> : "Set password and sign in"}</Button>
          <div className="flex justify-between gap-3">
            <button type="button" className={link} onClick={() => go("send-code")}>Send a new code</button>
            <button type="button" className={link} onClick={() => go("sign-in")}>Back to sign-in</button>
          </div>
        </form>
      )}
    </div>
  );
}

function passwordError(e: { code?: string; message?: string; status?: number }, name: string) {
  if (e.status === 429) return "Too many tries. Wait a minute, then try again.";
  switch (e.code) {
    case "INVALID_EMAIL_OR_PASSWORD":
      return "That email and password don't match. No password yet? Use \"Set or reset your password\".";
    case "EMAIL_NOT_VERIFIED":
      return "This account has no password yet. Use \"Set or reset your password\" to get a code.";
    case "INVALID_OTP":
    case "OTP_EXPIRED":
    case "TOO_MANY_ATTEMPTS":
      return "That code is wrong or has expired. Ask for a new one.";
    case "PASSWORD_TOO_SHORT":
      return `Use at least ${PASSWORD_MIN} characters.`;
    case "PASSWORD_TOO_LONG":
      return "That password is too long.";
    case "PASSWORD_COMPROMISED":
      return "This password has appeared in a data breach. Choose another one.";
    case "PASSWORD_GUESSABLE":
      return e.message ?? `Don't use ${name} or your own email name in your password.`;
  }
  return friendly(e.message ?? "Sign-in failed", name);
}

function friendly(code: string, name: string) {
  if (/not been invited|forbidden|unable_to_create_user/i.test(code)) return `Your account hasn't been invited to ${name} yet. Ask an admin to add you.`;
  if (/disabled/i.test(code)) return `Your ${name} account is disabled.`;
  return code.replaceAll("_", " ");
}
